// file: src/components/UI/inputs/NumericInputField.jsx

import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Stack } from "@metrea-ui/v2";
import { NumericFormatAdapter } from "../common/NumericFormatAdapter";

/**
 * NumericInputField
 *
 * - Controlled by `value` (number)
 * - Allows free typing internally
 * - Emits changes ONLY via `onCommit`
 * - Optional +/- controls
 *
 * IMPORTANT:
 * - Do NOT expose onChange upstream
 * - This component intentionally has ONE output channel: onCommit
 */
export default function NumericInputField({
	value,
	onCommit,
	min,
	max,
	step = 1,
	showControls = false,
	disabled = false,
	atMax,
	disableIncrement = false,
	disableDecrement = false,
	validationMessage,
	onError,
	...textFieldProps
}) {
	const inputRef = useRef(null);

	const [draft, setDraft] = useState(String(value));
	const [localError, setLocalError] = useState("");
	const lastCommittedRef = useRef(value);

	const {
		id,
		name,
		inputProps,
		label,
		size,
		margin,
		style,
		...restInputProps
	} = textFieldProps;

	const getControlCursorStyle = (isDisabled) => ({
		cursor: isDisabled ? "not-allowed" : "pointer",
	});

	/* ------------------------------------------------------------------
	 * Sync external value -> local draft
	 * (e.g. slider moved, scenario recalculated, reset)
	 * ------------------------------------------------------------------ */
	useEffect(() => {
		if (value !== lastCommittedRef.current) {
			lastCommittedRef.current = value;
			setDraft(String(value));
			setLocalError("");
		}
	}, [value]);

	/* ------------------------------------------------------------------
	 * Event handlers
	 * ------------------------------------------------------------------ */
	const commitFromControl = (rawNext) => {
		const next = Number(rawNext);

		if (!Number.isFinite(next)) return;

		if (min != null && next < min) {
			onError?.("overMin");
			return;
		}

		if (max != null && next > max) {
			onError?.("overMax");
			return;
		}

		lastCommittedRef.current = next;
		setDraft(String(next));
		setLocalError("");
		onCommit?.(next);
	};

	const validateAndCommit = (rawValue) => {
		const normalized =
			rawValue !== "" && rawValue != null && typeof rawValue === "string"
				? rawValue.replace(/[^0-9.-]/g, "")
				: rawValue;
		const next = Number(normalized);

		if (!Number.isFinite(next)) return;

		if (min != null && next < min) {
			setLocalError(validationMessage || `Value must be between ${min} and ${max}.`);
			lastCommittedRef.current = min;
			setDraft(String(min));
			onCommit?.(min, { reason: "revert", errorType: "overMin" });
			onError?.("overMin");
			return;
		}

		if (max != null && next > max) {
			setLocalError(validationMessage || `Value must be between ${min} and ${max}.`);
			lastCommittedRef.current = max;
			setDraft(String(max));
			onCommit?.(max, { reason: "revert", errorType: "overMax" });
			onError?.("overMax");
			return;
		}

		setLocalError("");
		lastCommittedRef.current = next;
		setDraft(String(next));
		onCommit?.(next, { reason: "commit" });
	};

	const handleIncrement = () => {
		if (!canIncrement) return;
		commitFromControl(lastCommittedRef.current + step);
	};

	const handleDecrement = () => {
		if (!canDecrement) return;
		commitFromControl(lastCommittedRef.current - step);
	};

	const canIncrement =
		!disabled && !disableIncrement && !(max != null && lastCommittedRef.current >= max) && !atMax;

	const canDecrement = !disabled && !disableDecrement && !(min != null && lastCommittedRef.current <= min);

	/* ------------------------------------------------------------------
	 * Render
	 * ------------------------------------------------------------------ */
	return (
		// @metrea-ui/v2 Stack consumes `gap` directly. Preserve the prior
		// spacing={1} behavior here by keeping the controls 8px apart explicitly.
		<Stack direction="row" align="center" gap="8px">
			{showControls && (
				<IconButton
					aria-label="Decrease"
					onClick={handleDecrement}
					disabled={!canDecrement}
					style={getControlCursorStyle(!canDecrement)}
					variant="solid"
					size="2xs"
				>
					-
				</IconButton>
			)}

			<Box display="flex" flexDirection="column" alignItems="center">
				<Box
					style={{
						width: "68px",
						backgroundColor: "var(--chakra-colors-color-palette-subtle, #e8edf2)",
						padding: "0 8px",
					}}
				>
					{/* Keep NumericFormatAdapter as the actual control here so this helper
					    preserves the existing raw numeric-string formatting contract
					    (including currency/unit support) after the old TextField wrapper retirement. */}
					<NumericFormatAdapter
						{...restInputProps}
						ref={inputRef}
						id={id}
						name={name}
						value={draft}
						disabled={disabled}
						inputMode="numeric"
						onChange={(e) => setDraft(e.target.value)}
						onBlur={(e) => validateAndCommit(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								validateAndCommit(e.currentTarget.value);
							}
						}}
						formatSpec={{
							kind: "number",
							symbol: "",
							unit: "",
							decimals: 0,
							fixed: false,
							allowNegative: false,
							min,
							max,
							...(inputProps?.formatSpec || {}),
						}}
						style={{
							width: "100%",
							minWidth: 0,
							textAlign: "center",
							background: "transparent",
							border: "none",
							outline: "none",
							fontSize: "0.8rem",
							padding: "6px 0",
							...(style || {}),
						}}
					/>
				</Box>
				{localError ? (
					<span
						style={{
							marginTop: "4px",
							fontSize: "0.75rem",
							lineHeight: 1.2,
							color: "var(--metrea-semantic-danger)",
							textAlign: "center",
							maxWidth: "96px",
						}}
					>
						{localError}
					</span>
				) : null}
			</Box>

			{showControls && (
				<IconButton
					aria-label="Increase"
					onClick={handleIncrement}
					disabled={!canIncrement}
					style={getControlCursorStyle(!canIncrement)}
					variant="solid"
					size="2xs"
				>
					+
				</IconButton>
			)}
		</Stack>
	);
}
